import { useState, useEffect } from "react";
import { useClub } from "@/pages/ClubLayout";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Users, UserCog, Trophy, Medal, Flag } from "lucide-react";

export default function ClubDashboard() {
  const ctx = useClub();
  const club = ctx?.club;
  const clubUser = ctx?.clubUser;
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/club/dashboard`, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setMetrics(data.metrics);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetch_();
  }, []);

  const METRIC_CARDS = [
    { label: "Jugadores", value: metrics?.total_players || 0, icon: Users, color: "bg-blue-100 text-blue-600" },
    { label: "Usuarios del Club", value: metrics?.total_users || 0, icon: UserCog, color: "bg-emerald-100 text-emerald-600" },
    { label: "Rankings", value: metrics?.total_rankings || 0, icon: Trophy, color: "bg-amber-100 text-amber-600" },
    { label: "Competencias", value: metrics?.total_competitions || 0, icon: Medal, color: "bg-purple-100 text-purple-600" },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Club header */}
      <div className="flex items-center gap-4 mb-8">
        {club?.logo_url ? (
          <img src={club.logo_url} alt={club.name} className="w-14 h-14 rounded-xl object-cover shadow" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-[#1B4D3E] flex items-center justify-center shadow">
            <Flag className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
        )}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A]" data-testid="club-dashboard-title">
            {club?.name || "Mi Club"}
          </h1>
          <p className="text-[#64748B] mt-0.5">
            Hola, {clubUser?.name?.split(" ")[0] || "Master"}
          </p>
        </div>
      </div>

      {/* Metrics */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="club-metrics-grid">
          {METRIC_CARDS.map((m) => {
            const Icon = m.icon;
            return (
              <Card key={m.label} className="card-default">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${m.color}`}>
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#0F172A]">{m.value}</p>
                      <p className="text-xs text-[#64748B]">{m.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Quick info */}
      <Card className="card-default">
        <CardContent className="p-6">
          <h2 className="text-base font-bold text-[#0F172A] mb-3">Informacion del Club</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
            <div>
              <span className="text-[#94A3B8] text-xs uppercase">Slug</span>
              <p className="text-[#0F172A] font-mono text-xs">/{club?.slug}</p>
            </div>
            <div>
              <span className="text-[#94A3B8] text-xs uppercase">Ciudad</span>
              <p className="text-[#0F172A]">{club?.city || "-"}{club?.country ? `, ${club.country}` : ""}</p>
            </div>
            <div>
              <span className="text-[#94A3B8] text-xs uppercase">Email</span>
              <p className="text-[#0F172A]">{club?.email || "-"}</p>
            </div>
            <div>
              <span className="text-[#94A3B8] text-xs uppercase">Telefono</span>
              <p className="text-[#0F172A]">{club?.phone || "-"}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
